import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  addFoodInvoiceLine,
  deleteFoodInvoiceLine,
  fetchFoodIngredients,
  fetchFoodInvoiceDetail,
  fetchFoodVendors,
  updateFoodInvoice,
  updateFoodInvoiceLine,
  type FoodIngredient,
  type FoodInvoiceDetail,
} from "../foodApi";
import { COMMON_UNITS, money } from "../utils/foodFormatters";
import {
  EMPTY_LINE,
  INPUT_CLASS,
  SMALL_BUTTON_CLASS,
  costPerUnitLabel,
  parseLineDraft,
  sumLineTotals,
  toLineDraft,
  totalsDiffer,
  type LineDraft,
} from "../utils/invoiceLines";
import { QueryError } from "../components/QueryError";

type LineItem = FoodInvoiceDetail["lineItems"][number];

function unitOptions(current: string): string[] {
  const units: string[] = [...COMMON_UNITS];
  return current && !units.includes(current) ? [current, ...units] : units;
}

function lineToDraft(line: LineItem): LineDraft {
  return toLineDraft({ ...line, ingredientId: line.ingredient?.id ?? null });
}

function sameDraft(a: LineDraft, b: LineDraft): boolean {
  return (Object.keys(a) as (keyof LineDraft)[]).every((key) => a[key] === b[key]);
}

export function InvoiceDetailView() {
  const params = useParams<{ id: string }>();
  const id = Number(params.id);
  const q = useQuery({
    queryKey: ["food", "invoice", id],
    queryFn: () => fetchFoodInvoiceDetail(id),
    enabled: Number.isFinite(id),
  });
  const ingredientsQuery = useQuery({
    queryKey: ["food", "ingredients"],
    queryFn: fetchFoodIngredients,
  });

  if (!Number.isFinite(id)) return <QueryError message="Invalid invoice id." />;
  if (q.isLoading) return <div className="p-6 text-tribal-muted">Loading invoice...</div>;
  if (q.isError || !q.data) {
    return <QueryError message={`Failed to load invoice: ${(q.error as Error).message}`} />;
  }

  const d: FoodInvoiceDetail = q.data;
  const linesTotal = sumLineTotals(d.lineItems.map((li) => ({ totalCost: String(li.totalCost) })));

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <InvoiceHeaderEditor invoice={d} />
      <section className="rounded-lg border border-tribal-border bg-tribal-surface">
        <div className="border-b border-tribal-border px-4 py-3 text-sm font-medium">
          Line items ({d.lineItems.length})
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-tribal-muted">
              <tr className="border-b border-tribal-border">
                <th className="px-2 py-2">Description</th>
                <th className="px-2 py-2">Quantity</th>
                <th className="px-2 py-2" title="What the vendor charged per billed unit">
                  Price
                </th>
                <th className="px-2 py-2">Total</th>
                <th className="px-2 py-2" title="Line total ÷ quantity">
                  Cost / unit
                </th>
                <th className="px-2 py-2">Ingredient</th>
                <th className="px-2 py-2" />
              </tr>
            </thead>
            <tbody className="divide-y divide-tribal-border/60">
              {d.lineItems.map((line) => (
                <ExistingLineRow
                  key={line.id}
                  line={line}
                  ingredients={ingredientsQuery.data ?? []}
                />
              ))}
              <NewLineRow invoiceId={d.id} ingredients={ingredientsQuery.data ?? []} />
            </tbody>
          </table>
        </div>
        <div className="space-y-1 border-t border-tribal-border px-4 py-3 text-sm">
          <p>
            Lines total <span className="font-semibold">{money(linesTotal)}</span> · Invoice total{" "}
            <span className="font-semibold">{money(d.totalAmount)}</span>
          </p>
          {totalsDiffer(linesTotal, d.totalAmount) && (
            <p className="text-amber-400">
              The lines don't add up to the invoice total. Adding, removing, or changing a line's
              total resets the invoice total to the sum of the lines; you can also set it above.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function useInvalidateFood() {
  const queryClient = useQueryClient();
  // Line changes move ingredient costs, menu costs and dashboard totals too.
  return () => queryClient.invalidateQueries({ queryKey: ["food"] });
}

function InvoiceHeaderEditor({ invoice }: { invoice: FoodInvoiceDetail }) {
  const invalidate = useInvalidateFood();
  const vendorsQuery = useQuery({ queryKey: ["food", "vendors"], queryFn: fetchFoodVendors });

  const initial = {
    vendorId: String(invoice.vendor.id),
    invoiceNumber: invoice.invoiceNumber ?? "",
    invoiceDate: invoice.invoiceDate.slice(0, 10),
    totalAmount: invoice.totalAmount == null ? "" : String(invoice.totalAmount),
  };
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setDraft(initial);
    // Reset when the saved values change (after a save or a line edit).
  }, [initial.vendorId, initial.invoiceNumber, initial.invoiceDate, initial.totalAmount]);

  const dirty = (Object.keys(initial) as (keyof typeof initial)[]).some(
    (key) => draft[key] !== initial[key],
  );

  const save = useMutation({
    mutationFn: () => {
      const total = draft.totalAmount.trim() === "" ? null : Number(draft.totalAmount);
      if (total !== null && !Number.isFinite(total)) throw new Error("Total must be a number");
      if (!draft.invoiceDate) throw new Error("Enter a date");
      return updateFoodInvoice(invoice.id, {
        vendorId: Number(draft.vendorId),
        invoiceNumber: draft.invoiceNumber.trim() || null,
        invoiceDate: draft.invoiceDate,
        totalAmount: total,
      });
    },
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
      <h2 className="text-lg font-semibold">{invoice.vendor.name}</h2>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="text-sm">
          <div className="mb-1 text-xs text-tribal-muted">Vendor</div>
          <select
            value={draft.vendorId}
            onChange={(e) => setDraft({ ...draft, vendorId: e.target.value })}
            className={`w-full ${INPUT_CLASS}`}
          >
            {(vendorsQuery.data ?? [invoice.vendor]).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-tribal-muted">Invoice #</div>
          <input
            value={draft.invoiceNumber}
            onChange={(e) => setDraft({ ...draft, invoiceNumber: e.target.value })}
            placeholder="Optional"
            className={`w-full ${INPUT_CLASS}`}
          />
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-tribal-muted">Date</div>
          <input
            type="date"
            value={draft.invoiceDate}
            onChange={(e) => setDraft({ ...draft, invoiceDate: e.target.value })}
            className={`w-full ${INPUT_CLASS}`}
          />
        </label>
        <label className="text-sm">
          <div className="mb-1 text-xs text-tribal-muted">Invoice Total ($)</div>
          <input
            inputMode="decimal"
            value={draft.totalAmount}
            onChange={(e) => setDraft({ ...draft, totalAmount: e.target.value })}
            className={`w-full ${INPUT_CLASS}`}
          />
        </label>
      </div>
      {error && <p className="mt-2 text-sm text-red-300">{error}</p>}
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!dirty || save.isPending}
          className="rounded-md bg-tribal-red px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
        >
          {save.isPending ? "Saving..." : "Save details"}
        </button>
        <button
          type="button"
          onClick={() => setDraft(initial)}
          disabled={!dirty || save.isPending}
          className={SMALL_BUTTON_CLASS}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function LineCells({
  draft,
  onChange,
  ingredients,
  disabled,
}: {
  draft: LineDraft;
  onChange: (patch: Partial<LineDraft>) => void;
  ingredients: FoodIngredient[];
  disabled: boolean;
}) {
  // Archived ingredients stay listed only for the line already using one.
  const options = ingredients.filter((i) => i.isActive || i.id === draft.ingredientId);
  return (
    <>
      <td className="px-2 py-2">
        <input
          aria-label="Description"
          value={draft.description}
          disabled={disabled}
          onChange={(e) => onChange({ description: e.target.value })}
          placeholder="Description"
          className={`w-56 ${INPUT_CLASS}`}
        />
      </td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5">
          <input
            inputMode="decimal"
            aria-label="Quantity"
            value={draft.quantity}
            disabled={disabled}
            onChange={(e) => onChange({ quantity: e.target.value })}
            className={`w-20 ${INPUT_CLASS}`}
          />
          <select
            aria-label="Unit"
            value={draft.unit}
            disabled={disabled}
            onChange={(e) => onChange({ unit: e.target.value })}
            className={INPUT_CLASS}
          >
            {unitOptions(draft.unit).map((unit) => (
              <option key={unit} value={unit}>
                {unit}
              </option>
            ))}
          </select>
        </div>
      </td>
      <td className="px-2 py-2">
        <input
          inputMode="decimal"
          aria-label="Price"
          value={draft.unitCost}
          disabled={disabled}
          onChange={(e) => onChange({ unitCost: e.target.value })}
          placeholder="Total ÷ qty"
          className={`w-24 ${INPUT_CLASS}`}
        />
      </td>
      <td className="px-2 py-2">
        <input
          inputMode="decimal"
          aria-label="Total"
          value={draft.totalCost}
          disabled={disabled}
          onChange={(e) => onChange({ totalCost: e.target.value })}
          className={`w-24 ${INPUT_CLASS}`}
        />
      </td>
      <td className="whitespace-nowrap px-2 py-3 text-tribal-muted">{costPerUnitLabel(draft)}</td>
      <td className="px-2 py-2">
        <select
          aria-label="Ingredient"
          value={draft.ingredientId ?? ""}
          disabled={disabled}
          onChange={(e) =>
            onChange({ ingredientId: e.target.value ? Number(e.target.value) : null })
          }
          className={`w-52 ${INPUT_CLASS}`}
        >
          <option value="">Unmapped</option>
          {options.map((ingredient) => (
            <option key={ingredient.id} value={ingredient.id}>
              {ingredient.name}
              {!ingredient.isActive ? " (archived)" : ""}
            </option>
          ))}
        </select>
      </td>
    </>
  );
}

function ExistingLineRow({ line, ingredients }: { line: LineItem; ingredients: FoodIngredient[] }) {
  const invalidate = useInvalidateFood();
  const saved = lineToDraft(line);
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<LineDraft>(saved);
  const [error, setError] = useState<string | null>(null);

  // Reset only when this line's saved values change, so a refetch after
  // saving another line doesn't wipe unsaved edits here.
  useEffect(() => {
    setDraft(JSON.parse(savedKey) as LineDraft);
  }, [savedKey]);

  const dirty = !sameDraft(draft, saved);

  const save = useMutation({
    mutationFn: () => {
      const parsed = parseLineDraft(draft);
      if (!parsed.ok) throw new Error(parsed.error);
      return updateFoodInvoiceLine(line.id, parsed.value);
    },
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  const remove = useMutation({
    mutationFn: () => deleteFoodInvoiceLine(line.id),
    onSuccess: () => invalidate(),
    onError: (e) => setError((e as Error).message),
  });

  const busy = save.isPending || remove.isPending;

  return (
    <tr className="align-top">
      <LineCells
        draft={draft}
        onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
        ingredients={ingredients}
        disabled={busy}
      />
      <td className="px-2 py-2">
        <div className="flex flex-wrap gap-1.5">
          {dirty ? (
            <>
              <button
                type="button"
                onClick={() => save.mutate()}
                disabled={busy}
                className="rounded-md bg-tribal-red px-2 py-1 text-xs font-medium text-white disabled:opacity-60"
              >
                {save.isPending ? "Saving..." : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setDraft(saved);
                  setError(null);
                }}
                disabled={busy}
                className={SMALL_BUTTON_CLASS}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Remove "${line.description}" from this invoice?`)) {
                  remove.mutate();
                }
              }}
              disabled={busy}
              className={SMALL_BUTTON_CLASS}
            >
              {remove.isPending ? "Removing..." : "Remove"}
            </button>
          )}
        </div>
        {error && <p className="mt-1 max-w-[10rem] text-xs text-red-300">{error}</p>}
      </td>
    </tr>
  );
}

function NewLineRow({ invoiceId, ingredients }: { invoiceId: number; ingredients: FoodIngredient[] }) {
  const invalidate = useInvalidateFood();
  const [draft, setDraft] = useState<LineDraft>(EMPTY_LINE);
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => {
      const parsed = parseLineDraft(draft);
      if (!parsed.ok) throw new Error(parsed.error);
      return addFoodInvoiceLine(invoiceId, parsed.value);
    },
    onSuccess: () => {
      setDraft(EMPTY_LINE);
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  return (
    <tr className="align-top bg-black/10">
      <LineCells
        draft={draft}
        onChange={(patch) => setDraft((prev) => ({ ...prev, ...patch }))}
        ingredients={ingredients}
        disabled={add.isPending}
      />
      <td className="px-2 py-2">
        <button
          type="button"
          onClick={() => add.mutate()}
          disabled={add.isPending || !draft.description.trim()}
          className={SMALL_BUTTON_CLASS}
        >
          {add.isPending ? "Adding..." : "Add line"}
        </button>
        {error && <p className="mt-1 max-w-[10rem] text-xs text-red-300">{error}</p>}
      </td>
    </tr>
  );
}
